import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { pl } from "date-fns/locale";
import { CheckCircle2, ChevronLeft, ChevronRight, PhoneCall, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { formatDate } from "@/utils/formatDate";
import { EmptyState, PageHeader, SectionCard } from "../components/ui";
import {
  useControlCallManual,
  useControlCalls,
  useMarkControlCallDone,
  useSelectableEmployees,
} from "../hooks";
import { apiErr } from "../utils";
import type {
  ControlCallEvent,
  ControlCallReminder,
  ControlCallSubjectKind,
} from "../types";

/* „Ponaglenia" (/power-projects/ponaglenia) — kalendarz telefonów kontrolnych DLC_01.
   Każdy dzień pokazuje TRZY rzeczy: co tego dnia wyszło (wnioski, wypisy, MPZP, mapy
   zasadnicze, MDCP, pakiety WPW), co wróciło, i które sprawy trzeba tego dnia ponaglić.
   Ponaglenie rodzi się samo w chwili złożenia — z terminem właściwym dla rejestru —
   a nieodklikane przechodzi na dzisiaj, pamiętając, kiedy wypadło pierwszy raz.
   Sprawy z Korespondencji trafiają tu z wyboru („Dodaj do ponagleń", termin = Termin KPA),
   a ponaglenie własne dodaje się ręcznie dla siebie — bez sprawy, kończy się po odklikaniu. */

const DAY_KEY = "yyyy-MM-dd";

/** Rejestry, które kalendarz umie pokazać — kolejność jak w menu modułu. */
const KINDS: { key: ControlCallSubjectKind; label: string }[] = [
  { key: "application", label: "Wnioski urzędowe" },
  { key: "wpw", label: "Pakiety WPW" },
  { key: "wypis", label: "Wypisy EGiB" },
  { key: "mpzp", label: "MPZP" },
  { key: "mz", label: "Mapy zasadnicze" },
  { key: "mdcp", label: "MDCP" },
  { key: "proceeding", label: "Sprawy" },
  { key: "manual", label: "Własne" },
];

/** Kolor rodzaju sprawy — ten sam w kafelku zdarzenia i ponaglenia, żeby dało się
 *  czytać miesiąc jednym rzutem oka. */
const KIND_COLOR: Record<string, string> = {
  application: "bg-sky-100 text-sky-900 border-sky-300",
  wpw: "bg-violet-100 text-violet-900 border-violet-300",
  wypis: "bg-emerald-100 text-emerald-900 border-emerald-300",
  mpzp: "bg-teal-100 text-teal-900 border-teal-300",
  mz: "bg-indigo-100 text-indigo-900 border-indigo-300",
  mdcp: "bg-amber-100 text-amber-900 border-amber-300",
  proceeding: "bg-rose-100 text-rose-900 border-rose-300",
  manual: "bg-slate-100 text-slate-900 border-slate-300",
};

function monthRange(anchor: Date) {
  return {
    start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }),
  };
}

export function ControlCallsPage() {
  const [anchor, setAnchor] = useState(new Date());
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [doneTarget, setDoneTarget] = useState<ControlCallReminder | null>(null);
  const [adding, setAdding] = useState(false);
  const manual = useControlCallManual();
  const removeManual = (r: ControlCallReminder) => {
    if (!window.confirm(`Usunąć ponaglenie własne „${r.title}”?`)) return;
    manual.remove.mutate(r.id, { onError: (e) => window.alert(apiErr(e)) });
  };
  // Domyślnie kalendarz jest LISTĄ ZADAŃ: same ponaglenia. Złożone i zakończone sprawy
  // (kronika rejestrów) dokłada się świadomie — i dopiero wtedy backend je liczy.
  const [showReminders, setShowReminders] = useState(true);
  const [showEvents, setShowEvents] = useState(false);
  // Podgląd dyrekcji: czyje zestawienie oglądamy. Pusty = własne.
  const [employee, setEmployee] = useState("");
  const [kinds, setKinds] = useState<Set<ControlCallSubjectKind>>(
    () => new Set(KINDS.map((k) => k.key)),
  );

  const toggleKind = (key: ControlCallSubjectKind) =>
    setKinds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const { start, end } = useMemo(() => monthRange(anchor), [anchor]);
  const { data, isLoading } = useControlCalls({
    from: format(start, DAY_KEY),
    to: format(end, DAY_KEY),
    scope,
    show: [showReminders && "reminders", showEvents && "events"].filter(Boolean).join(",") || "none",
    kinds: [...kinds].join(",") || "none",
    user: employee,
  });

  const canPickUser = data?.meta.can_pick_user ?? false;
  // Listę pracowników ściągamy dopiero, gdy backend potwierdzi prawo wyboru.
  const { data: employees } = useSelectableEmployees(canPickUser);

  const days = useMemo(() => eachDayOfInterval({ start, end }), [start, end]);

  const remindersByDay = useMemo(() => {
    const map = new Map<string, ControlCallReminder[]>();
    for (const r of data?.data.reminders ?? []) {
      const list = map.get(r.due_date) ?? [];
      list.push(r);
      map.set(r.due_date, list);
    }
    return map;
  }, [data]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, ControlCallEvent[]>();
    for (const e of data?.data.events ?? []) {
      const list = map.get(e.day) ?? [];
      list.push(e);
      map.set(e.day, list);
    }
    return map;
  }, [data]);

  const pendingDue = data?.meta.pending_due ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ponaglenia"
        subtitle="Telefony kontrolne do urzędów i podwykonawców — kalendarz spraw złożonych, zakończonych i tych, o które trzeba dopytać · DLC_01"
        actions={
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus size={14} className="mr-1" />
            Ponaglenie własne
          </Button>
        }
      />

      <SectionCard
        title={format(anchor, "LLLL yyyy", { locale: pl })}
        action={
          <div className="flex items-center gap-2">
            {canPickUser && (
              <select
                className="input-base !py-0.5 text-xs"
                value={employee}
                onChange={(e) => setEmployee(e.target.value)}
                aria-label="Zestawienie pracownika"
              >
                <option value="">Moje zestawienie</option>
                {(employees ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {`${u.first_name} ${u.last_name}`.trim() || u.username}
                  </option>
                ))}
              </select>
            )}
            <div className="inline-flex border border-[var(--color-border)] text-xs font-mono uppercase tracking-wider">
              {([["mine", "Moje"], ["all", "Wszystkie"]] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  // Wskazana osoba wygrywa ze zakresem — przełącznik wraca wtedy do gry
                  // dopiero po zdjęciu filtra pracownika.
                  disabled={!!employee}
                  onClick={() => setScope(key)}
                  className={`px-3 py-1 disabled:opacity-40 ${
                    !employee && scope === key
                      ? "bg-[var(--color-primary)] text-white"
                      : "text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="p-1 border border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
              aria-label="Poprzedni miesiąc"
              onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="px-2 py-1 text-xs border border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
              onClick={() => setAnchor(new Date())}
            >
              Dziś
            </button>
            <button
              type="button"
              className="p-1 border border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
              aria-label="Następny miesiąc"
              onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        }
      >
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <div className="flex items-center gap-3">
            <span className="font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Pokaż
            </span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showReminders}
                onChange={(e) => setShowReminders(e.target.checked)}
              />
              Ponaglenia
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showEvents}
                onChange={(e) => setShowEvents(e.target.checked)}
              />
              Złożone i zakończone sprawy
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Rejestry
            </span>
            {KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                onClick={() => toggleKind(k.key)}
                aria-pressed={kinds.has(k.key)}
                className={`px-2 py-0.5 border ${
                  kinds.has(k.key)
                    ? KIND_COLOR[k.key]
                    : "border-[var(--color-border)] text-[var(--color-text-muted)] opacity-60"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>

        {data?.meta.user && (
          <div className="mb-3 text-xs border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
            Zestawienie pracownika: <strong>{data.meta.user.name}</strong> — ponaglenia
            odklikuje osoba, do której należą.
          </div>
        )}

        {pendingDue > 0 && (
          <div className="mb-3 text-xs border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2">
            <PhoneCall size={13} className="inline mr-1 -mt-0.5" />
            {pendingDue === 1
              ? "1 ponaglenie do wykonania"
              : `${pendingDue} ponagleń do wykonania`}
            {" "}— nieodklikane przechodzą na kolejny dzień.
          </div>
        )}

        {isLoading ? (
          <div className="py-10 flex justify-center"><Spinner /></div>
        ) : (
          <div className="grid grid-cols-7 gap-px bg-[var(--color-border)] border border-[var(--color-border)]">
            {["pon", "wt", "śr", "czw", "pt", "sob", "ndz"].map((d) => (
              <div
                key={d}
                className="bg-[var(--color-bg-surface)] px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]"
              >
                {d}
              </div>
            ))}
            {days.map((day) => {
              const key = format(day, DAY_KEY);
              return (
                <DayCell
                  key={key}
                  day={day}
                  inMonth={isSameMonth(day, anchor)}
                  reminders={remindersByDay.get(key) ?? []}
                  events={eventsByDay.get(key) ?? []}
                  onTickOff={setDoneTarget}
                  onDelete={removeManual}
                />
              );
            })}
          </div>
        )}

        {!isLoading && !data?.data.reminders.length && !data?.data.events.length && (
          <div className="mt-3">
            <EmptyState>
              {showReminders || showEvents
                ? "W tym miesiącu nie ma nic do pokazania przy wybranych filtrach."
                : "Wszystko odfiltrowane — zaznacz „Ponaglenia” albo „Złożone i zakończone sprawy”."}
            </EmptyState>
          </div>
        )}
      </SectionCard>

      <MarkDoneModal reminder={doneTarget} onClose={() => setDoneTarget(null)} />
      <AddManualModal open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function DayCell({
  day,
  inMonth,
  reminders,
  events,
  onTickOff,
  onDelete,
}: {
  day: Date;
  inMonth: boolean;
  reminders: ControlCallReminder[];
  events: ControlCallEvent[];
  onTickOff: (r: ControlCallReminder) => void;
  onDelete: (r: ControlCallReminder) => void;
}) {
  return (
    <div
      className={`min-h-[7rem] p-1.5 space-y-1 ${
        inMonth ? "bg-[var(--color-bg-surface)]" : "bg-[var(--color-bg-elevated)] opacity-60"
      }`}
    >
      <div
        className={`text-[11px] font-mono ${
          isToday(day)
            ? "inline-block bg-[var(--color-primary)] text-white px-1.5"
            : "text-[var(--color-text-muted)]"
        }`}
      >
        {format(day, "d")}
      </div>

      {reminders.map((r) => (
        <ReminderChip key={r.id} reminder={r} onTickOff={onTickOff} onDelete={onDelete} />
      ))}

      {events.map((e, i) => (
        <Link
          key={`${e.url}-${e.event}-${i}`}
          to={e.url}
          title={e.title}
          className={`block truncate text-[11px] px-1 py-0.5 border-l-2 hover:underline ${
            KIND_COLOR[e.subject_kind] ?? "bg-gray-100 text-gray-900 border-gray-300"
          } ${e.event === "done" ? "opacity-70" : ""}`}
        >
          {e.event === "done" ? "✓ " : "↗ "}
          {e.title}
        </Link>
      ))}
    </div>
  );
}

function ReminderChip({
  reminder,
  onTickOff,
  onDelete,
}: {
  reminder: ControlCallReminder;
  onTickOff: (r: ControlCallReminder) => void;
  onDelete: (r: ControlCallReminder) => void;
}) {
  const done = reminder.status === "done";
  // Własne nie ma sprawy: bez linku, bez „leży N dni", za to da się je usunąć.
  const own = reminder.subject_kind === "manual";
  const label = own
    ? "Własne · "
    : reminder.sequence_no > 1
      ? `${reminder.sequence_no}. ponaglenie · `
      : "Ponaglenie · ";
  const rolledFrom = reminder.rolled_over ? ` · z ${formatDate(reminder.first_due_date)}` : "";
  return (
    <div
      className={`text-[11px] border px-1 py-0.5 ${
        done
          ? "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]"
          : "border-amber-400 bg-amber-50 text-amber-900"
      }`}
    >
      <div className="flex items-start gap-1">
        {done ? (
          <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
        ) : (
          <button
            type="button"
            onClick={() => onTickOff(reminder)}
            title="Odklikaj: ponaglenie wykonane"
            className="mt-0.5 shrink-0 w-3 h-3 border border-amber-600 hover:bg-amber-200"
            aria-label={`Oznacz ponaglenie jako wykonane: ${reminder.title}`}
          />
        )}
        {reminder.url ? (
          <Link to={reminder.url} className="truncate hover:underline" title={reminder.title}>
            {label}
            {reminder.title}
          </Link>
        ) : (
          <span className="truncate" title={reminder.title}>
            {label}
            {reminder.title}
          </span>
        )}
        {own && !done && (
          <button
            type="button"
            onClick={() => onDelete(reminder)}
            title="Usuń ponaglenie własne"
            aria-label={`Usuń ponaglenie własne: ${reminder.title}`}
            className="ml-auto mt-0.5 shrink-0 opacity-60 hover:opacity-100"
          >
            <X size={11} />
          </button>
        )}
      </div>
      <div className="pl-4 text-[10px] opacity-80">
        {done
          ? `ponaglone${reminder.note ? ` — ${reminder.note}` : ""}`
          : own
            ? `dla siebie${rolledFrom}`
            : `leży ${reminder.days_waiting} dni${rolledFrom}`}
      </div>
    </div>
  );
}

function MarkDoneModal({
  reminder,
  onClose,
}: {
  reminder: ControlCallReminder | null;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  const mutation = useMarkControlCallDone();

  const close = () => {
    setNote("");
    onClose();
  };

  return (
    <Modal open={!!reminder} onClose={close} title="Ponaglenie wykonane" size="md">
      {reminder && (
        <div className="space-y-3">
          <div className="text-sm">
            <div className="font-bold">{reminder.title}</div>
            <div className="text-xs text-[var(--color-text-muted)]">
              {reminder.subject_kind_display}
              {reminder.subject_kind !== "manual" && ` · leży ${reminder.days_waiting} dni`}
              {reminder.rolled_over &&
                ` · ponaglenie z ${format(new Date(reminder.first_due_date), "d.MM.yyyy")}`}
            </div>
          </div>

          <label className="block text-xs">
            <span className="text-[var(--color-text-muted)]">Uwaga (opcjonalnie)</span>
            <textarea
              className="input-base w-full mt-1 text-sm"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Np. referent prosi o tydzień, sprawa u radcy prawnego…"
            />
          </label>

          <p className="text-xs text-[var(--color-text-muted)]">
            {reminder.subject_kind === "manual"
              ? "Ponaglenie własne kończy się po odklikaniu — kolejnego nie będzie."
              : "Po odklikaniu kalendarz zaplanuje kolejne ponaglenie w tym samym rytmie — chyba że sprawa się wcześniej zakończy."}
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>Anuluj</Button>
            <Button
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate(
                  { id: reminder.id, note },
                  { onSuccess: close },
                )
              }
            >
              Ponaglone
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Ponaglenie własne: czego dotyczy i na kiedy. Zawsze dla zalogowanego — backend
 *  i tak przypisuje je pytającemu. */
function AddManualModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const tomorrow = () => format(addDays(new Date(), 1), DAY_KEY);
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState(tomorrow);
  const { create } = useControlCallManual();

  const close = () => {
    setTitle("");
    setDueDate(tomorrow());
    create.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title="Ponaglenie własne" size="md">
      <div className="space-y-3">
        <label className="block text-xs">
          <span className="text-[var(--color-text-muted)]">Czego dotyczy</span>
          <input
            className="input-base w-full mt-1 text-sm"
            value={title}
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Np. zadzwonić do ZUD w sprawie uzgodnienia trasy"
          />
        </label>
        <label className="block text-xs">
          <span className="text-[var(--color-text-muted)]">Termin</span>
          <input
            type="date"
            className="input-base w-full mt-1 text-sm"
            value={dueDate}
            min={format(new Date(), DAY_KEY)}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
        <p className="text-xs text-[var(--color-text-muted)]">
          Trafi do rejestru „Własne” na Twojej liście. Nieodklikane przechodzi na kolejny
          dzień, odklikane się kończy.
        </p>
        {create.isError && (
          <p className="text-xs text-red-600">{apiErr(create.error)}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>Anuluj</Button>
          <Button
            disabled={!title.trim() || !dueDate}
            loading={create.isPending}
            onClick={() =>
              create.mutate({ title: title.trim(), due_date: dueDate }, { onSuccess: close })
            }
          >
            Dodaj ponaglenie
          </Button>
        </div>
      </div>
    </Modal>
  );
}
